import React, { useState } from 'react';
import { Check, ArrowRight, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

const PlanCard = ({ 
  plan, 
  billingPeriod,
  currentBillingPeriod,
  isCurrentPlan,
  isCurrentPlanDifferentPeriod,
  isFoundingMember = false,
  onSelectPlan, 
  disabled 
}) => {
  const [showFeatures, setShowFeatures] = useState(false);
  
  const isPro = plan.name === 'Pro' || plan.name === 'Professional';

  // Founder pricing applies to Pro tier only, monthly only
  const founderRate = 10.00;
  const isFounderPricingApplicable = isFoundingMember && isPro && billingPeriod === 'monthly';

  const standardPrice = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
  const price = isFounderPricingApplicable ? founderRate : standardPrice;
  const isEnterprise = plan.name === 'Enterprise';
  const isSolo = plan.name === 'Solo' || plan.name === 'Starter';
  
  const getButtonText = () => {
    if (isCurrentPlan) return 'Current Plan';
    if (isCurrentPlanDifferentPeriod) {
      return billingPeriod === 'annual' ? 'Switch to Annual' : 'Switch to Monthly';
    }
    if (isPro && !isCurrentPlan) return 'Upgrade';
    if (isEnterprise && !isCurrentPlan) return 'Upgrade';
    if (isSolo && !isCurrentPlan) return 'Downgrade';
    return 'Select Plan';
  };

  const getButtonClass = () => {
    if (isCurrentPlan) {
      return 'bg-gray-200 text-gray-600 cursor-not-allowed';
    }
    if (isCurrentPlanDifferentPeriod) {
      return 'bg-green-600 text-white hover:bg-green-700';
    }
    if (isPro || isEnterprise) {
      return 'bg-blue-600 text-white hover:bg-blue-700';
    }
    return 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50';
  };

  const getButtonIcon = () => {
    if (isCurrentPlan) return null;
    if (isCurrentPlanDifferentPeriod) return <RefreshCw className="ml-2 h-4 w-4" />;
    return <ArrowRight className="ml-2 h-4 w-4" />;
  };

  const getCardClass = () => {
    if (isCurrentPlan) {
      return 'border-green-500 ring-2 ring-green-200';
    }
    if (isCurrentPlanDifferentPeriod) {
      return 'border-blue-500 ring-2 ring-blue-200';
    }
    return 'border-gray-200 hover:border-blue-300';
  };

  return (
    <div className={`relative bg-white rounded-xl shadow-sm border-2 transition-all ${getCardClass()}`}>
      
      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
            <Check size={12} />
            Current Plan
          </span>
        </div>
      )}

      {/* Switch Billing Period Badge */}
      {isCurrentPlanDifferentPeriod && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
            <RefreshCw size={12} />
            {billingPeriod === 'annual' ? 'Switch & Save!' : 'Your Plan'}
          </span>
        </div>
      )}

      <div className={`p-4 sm:p-6 ${(isCurrentPlan || isCurrentPlanDifferentPeriod) ? 'pt-6' : 'pt-4 sm:pt-6'}`}>
        
        {/* Plan Name */}
        <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
          {plan.name}
        </h3>

        {/* Price */}
        <div className="text-center mb-4">
          {isFounderPricingApplicable ? (
            <>
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-lg text-gray-400 line-through">
                  {formatCurrency(standardPrice)}
                </span>
                <span className="text-3xl font-bold text-amber-600">
                  {formatCurrency(price)}
                </span>
                <span className="text-gray-600 text-sm">/mo</span>
              </div>
              <p className="text-xs text-amber-700 font-semibold mt-1 flex items-center justify-center gap-1">
                ⭐ Founder rate — locked for life
              </p>
            </>
          ) : (
            <>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-3xl font-bold text-gray-900">
                  {formatCurrency(price)}
                </span>
                <span className="text-gray-600 text-sm">
                  /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                </span>
              </div>
              {billingPeriod === 'annual' && plan.monthlyPrice && (
                <p className="text-xs text-green-600 font-medium mt-1">
                  Save ${((plan.monthlyPrice * 12) - plan.annualPrice).toFixed(0)}/year
                </p>
              )}
            </>
          )}
        </div>

        {/* Features - Collapsible on mobile */}
        <div className="mb-4">
          {/* Mobile: Show toggle button */}
          <button
            onClick={() => setShowFeatures(!showFeatures)}
            className="sm:hidden w-full flex items-center justify-between py-2 text-sm text-gray-600 border-t border-gray-100"
          >
            <span>{plan.features?.length || 0} features included</span>
            {showFeatures ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {/* Mobile: Collapsible features */}
          <ul className={`space-y-2 sm:block ${showFeatures ? 'block mt-2' : 'hidden'}`}>
            {plan.features?.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                  (isPro || isEnterprise) ? 'text-blue-600' : 'text-green-600'
                }`} />
                <span className="text-sm text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Action Button */}
        <button
          onClick={() => onSelectPlan(plan)}
          disabled={disabled || isCurrentPlan}
          className={`w-full px-4 py-2.5 font-semibold rounded-lg transition-all text-sm ${getButtonClass()} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
        >
          {getButtonText()}
          {getButtonIcon()}
        </button>
      </div>
    </div>
  );
};

export default PlanCard;